import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {resolve} from '../../../api/endPoints';
import axiosClient from '../../../utils/axios';
import {readLomavisCreatorConfig} from '../lomavisCreator/lomavisCreatorConfig';
import {
  APPROVAL_USERS_RULE_ANY,
  CONFIG_SELECT_OPTIONS,
  RIGHT_COLUMN_OPTIONS,
  STATUS_ACTIVE,
  STATUS_DRAFT,
  STEP_MODE_SINGLE_STEP,
  TableSelectOption,
  TARGET_PUBLISH_WITH_SCHEDULE
} from './constants';
import {fetchAllPaginatedResults} from "../../../utils/http/fetchAllPaginatedResults";

type TargetType = 'publish' | 'save_as_approved_post';
type SelectType = TableSelectOption.USER | TableSelectOption.GROUP;
type CurrentStepType = Number;
const creator_config = readLomavisCreatorConfig();
interface LomavisPostApprovalState {
  loading: boolean;
  error: string | null;
  ui: Record<string, any>;
  featuresEnabled: {
    approvalProcessMultistep: boolean,
    approvalProcessSaveAsApprovedPost: boolean,
    approvalProcessGroups: boolean,
  },
  local_states: {
    selected_config_tab: String;
    delete_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    local_changes: boolean;
    right_column_options: String;
  };
  data: {
    approval_process_final_decision_timestamp?: boolean|null;
    cloud_uuid?: string;
    location_uuid?: string;
    is_multipost?: boolean;
    step_mode: Number;
    target: Number;
    status: Number;
    private_message: any;
    public_message: any;
    select_type: SelectType;
    current_step: CurrentStepType;
    create_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    edit_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    users: any[];
    groups: any[];

    approval_step_1_users_rule: number;
    approval_step_2_users_rule: number;
    approval_step_1_users: any[];
    approval_step_2_users: any[];
    approval_step_2_group: any[];
    approval_step_1_group: any[];
  };
}

export const LomavisPostApprovalStateInitialState: LomavisPostApprovalState = {
  loading: false,
  error: null,
  featuresEnabled: {
    approvalProcessMultistep: false,
    approvalProcessSaveAsApprovedPost: false,
    approvalProcessGroups: false,
  },
  ui: {
    showPostApprovalCreatorModal: false,
    showCreateGroupModal: false,
    showEditGroupModal: false,
    showDeleteConfirmationModal: false
  },
  local_states: {
    selected_config_tab: CONFIG_SELECT_OPTIONS.CONFIG,
    delete_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: 1,
      has_been_saved: false
    },
    local_changes: false,
    right_column_options: RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_USERS
  },

  data: {
    cloud_uuid: creator_config?.cloud?.uuid,
    location_uuid: creator_config?.location?.uuid,
    is_multipost: creator_config?.is_multipost,
    step_mode: STEP_MODE_SINGLE_STEP,
    target: TARGET_PUBLISH_WITH_SCHEDULE,
    status: STATUS_DRAFT,
    private_message: { ops: [{ insert: '\n' }] },
    public_message: { ops: [{ insert: '\n' }] },
    select_type: TableSelectOption.USER,
    current_step: 1,
    edit_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: undefined,
      has_been_saved: false
    },
    create_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: APPROVAL_USERS_RULE_ANY,
      has_been_saved: false
    },
    users: [],
    groups: [],

    approval_step_1_users_rule: APPROVAL_USERS_RULE_ANY,
    approval_step_2_users_rule: APPROVAL_USERS_RULE_ANY,
    approval_step_1_users: [],
    approval_step_2_users: [],
    approval_step_2_group: null,
    approval_step_1_group: null
  }
};

const lomavisPostApprovalSlice = createSlice({
  name: 'lomavisPostApproval',
  initialState: LomavisPostApprovalStateInitialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setApprovalUI(state, action: PayloadAction<{ key: string; value: any }>) {
      state.ui[action.payload.key] = action.payload.value;
    },
    setApprovalConfiguration(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.data[action.payload.key] = action.payload.value;
    },
    setApprovalKey(state, action: PayloadAction<{ key: string; value: any }>) {
      state.data[action.payload.key] = action.payload.value;
    },
    setGroupValue(state, action: PayloadAction<{ key: string; value: any }>) {
      state.data.create_group_data[action.payload.key] = action.payload.value;
    },
    setEditGroupValue(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.data.edit_group_data[action.payload.key] = action.payload.value;
    },
    setGroupForEdit(state, action: PayloadAction<any>) {
      state.data.edit_group_data = action.payload;
    },
    setGroupForDelete(state, action: PayloadAction<any>) {
      state.local_states.delete_group_data = action.payload;
    },
    setUsers(state, action: PayloadAction<any>) {
      state.data.users = action.payload;
    },
    setGroups(state, action: PayloadAction<any>) {
      state.data.groups = action.payload;
    },
    updateGroups(state, action: PayloadAction<any>) {
      state.data.groups = [action.payload, ...state.data.groups];
    },
    resetGroupData(state) {
      state.data.create_group_data = {
        uuid: '',
        name: '',
        members: [],
        approval_step_users_rule: 1,
        has_been_saved: false
      };
    },
    setLocalStates(state, action: PayloadAction<{ key: string; value: any }>) {
      state.local_states[action.payload.key] = action.payload.value;
    },
    loadApprovalData(state, action: PayloadAction<any>) {
      state.data = {
        ...state.data, // Keep existing values
        ...action.payload // Override only present keys
      };

      // IF the approval is running, directly show the progress
      if (state?.data?.status === STATUS_ACTIVE || state?.data?.approval_process_final_decision_timestamp) {
        state.local_states.right_column_options = RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_PROGRESS;
      }else{
        state.local_states.right_column_options = RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_USERS;
      }

      // Initialize the right user/group selectors based on the data
      if (state?.data?.approval_step_1_group) {
        state.data.select_type = TableSelectOption.GROUP;
      }
    },
    loadApprovalFeatures(state, action: PayloadAction<any>) {
        state.featuresEnabled = {
            approvalProcessMultistep: action.payload?.approvalProcessMultistep,
            approvalProcessSaveAsApprovedPost: action.payload?.approvalProcessSaveAsApprovedPost,
          approvalProcessGroups: action.payload?.approvalProcessGroups,
        };
    },
    resetApprovalData(state) {
        state.data = {
            ...LomavisPostApprovalStateInitialState.data,
            cloud_uuid: state.data.cloud_uuid,
            location_uuid: state.data.location_uuid,
            is_multipost: state.data.is_multipost,
            users: state.data.users,
            groups: state.data.groups,
        };
    }
  }
});

export const {
  setLoading,
  setError,
  setApprovalUI,
  setApprovalConfiguration,
  setApprovalKey,
  setGroupValue,
  setEditGroupValue,
  setGroupForEdit,
  setGroupForDelete,
  setUsers,
  setGroups,
  updateGroups,
  setLocalStates,
  loadApprovalData,
  loadApprovalFeatures,
  resetApprovalData
} = lomavisPostApprovalSlice.actions;
export default lomavisPostApprovalSlice.reducer;

export const fetchUsers =
  (data: {
    locationUuid?: string;
    cloudUuid?: string;
    is_multipost?: boolean;
  }) =>
  async (dispatch: any) => {
    dispatch(setLoading(true));
    const payload: Record<string, any> = {};
    if (data.is_multipost) payload.is_multipost = data.is_multipost;
    if (data.cloudUuid) payload.cloud__uuid = data.cloudUuid;
    if (data.locationUuid) payload.location__uuid = data.locationUuid;
    const url = resolve('approval_process_users', {}, payload);

    try {
      const users = await fetchAllPaginatedResults(url, {}) || []
      dispatch(setUsers(users));
    } catch (error: any) {
      dispatch(setError(error));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchGroups =
  (data: {
    locationUuid?: string;
    cloudUuid?: string;
    is_multipost?: boolean;
  }) =>
  async (dispatch: any) => {
    dispatch(setLoading(true));
    const payload: Record<string, any> = {};
    if (data.is_multipost) payload.is_multipost = data.is_multipost;
    if (data.cloudUuid) payload.cloud__uuid = data.cloudUuid;
    if (data.locationUuid) payload.location__uuid = data.locationUuid;
    const url = resolve('approval_process_groups', {}, payload);
    try {
      const groups = await fetchAllPaginatedResults(url, {}) || []
      dispatch(setGroups(groups));
    } catch (error: any) {
      dispatch(setError(error));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const createGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const url = resolve('approval_process_groups', {}, {});
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.data.create_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  try {
    const response = await axiosClient.post(url, groupData);
    dispatch(updateGroups(response?.data));
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};
export const editGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.data.edit_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  const payload: Record<string, any> = {};
  if (groupData.is_multipost) payload.is_multipost = groupData.is_multipost;
  if (groupData.cloud) payload.cloud__uuid = groupData.cloud;
  if (groupData.location) payload.location__uuid = groupData.location;

  try {
    const url = resolve(
      'approval_process_groups_edit',
      { uuid: groupData.uuid },
      payload
    );
    const response = await axiosClient.put(url, groupData);

    dispatch(
      fetchGroups({
        locationUuid: groupData.location,
        cloudUuid: groupData.cloud,
        is_multipost: groupData.is_multipost
      })
    );
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.local_states.delete_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  const payload: Record<string, any> = {};
  if (groupData.is_multipost) payload.is_multipost = groupData.is_multipost;
  if (groupData.cloud) payload.cloud__uuid = groupData.cloud;
  if (groupData.location) payload.location__uuid = groupData.location;

  try {
    const url = resolve(
      'approval_process_groups_edit',
      { uuid: groupData.uuid },
      payload
    );
    const response = await axiosClient.delete(url, groupData);

    dispatch(
      fetchGroups({
        locationUuid: groupData.location,
        cloudUuid: groupData.cloud,
        is_multipost: groupData.is_multipost
      })
    );
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};

export const resetGroupData = () => async (dispatch: any) => {
  dispatch(setLoading(true));
  dispatch(lomavisPostApprovalSlice.actions.resetGroupData());
  dispatch(setLoading(false));
};
